import Vue from 'vue';
import Router from 'vue-router';
import i18n from '@/i18n/i18n';
import enums from '@/enums';
import store from '../store/store';
import routes from './routes';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes,
});

router.afterEach((to, from) => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }

  Vue.nextTick(() => {
    // Hosted public pages (waitlist/review/privacy policy) manage their own async title + page view
    // after branding loads. Skip router-level title + custom_page_view to avoid stale titles.
    if (to.meta && to.meta.hostedPublicPage) return;

    const brand = i18n.t('$Brand.Name') || '';
    const title = to.meta.pageTitle ? i18n.tc(
      to.meta.pageTitle,
      to.meta.i18nPageTitle || 2,
    ) || '' : '';
    const sep = title ? ' | ' : '';
    document.title = `${title}${sep}${brand}`;

    // Track manual page view in GTM (Mirrors website implementation)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'custom_page_view',
      page_path: to.fullPath,
      page_title: document.title,
    });
  });
});

router.beforeEach(async (to, from, next) => {
  // Check staging authentication first
  if (process.env.VUE_APP_ENV === enums.AppEnvironments.STAGING) {
    const { checkStagingAuth } = await import('@/utils/staging-auth.util');
    const isAuthenticated = await checkStagingAuth();

    if (!isAuthenticated) {
      return; // Block navigation until authentication is successful
    }
  }

  if (to.matched.some((route) => route.meta.requiresAuth)) {
    if (!store.getters['auth/isUserLoggedIn']) {
      if (to.query && to.query.loginRedirect) {
        store.commit('UPDATE_REDIRECT_AFTER_LOGIN', to.query.loginRedirect);
      }

      store.dispatch('resetBreadcrumb');
      next({ name: 'login' });
    } else if (to.matched.some((route) => route.meta && route.meta.roles)) {
      const roleName = store.getters['auth/role'];
      let validPermission = true;

      to.matched.forEach((route) => {
        if ((route.meta && route.meta.roles && !route.meta.roles.includes(roleName))) {
          validPermission = false;
        }
      });

      if (validPermission) {
        if (store.getters['auth/getUserFlag']('acceptTermsAndConditions')) {
          store.dispatch('updateBreadcrumb', { to, from });
          store.dispatch('updateNavigationHistory', { to });
          next();
        } else if (!to.matched.some((route) => route.meta.isTermsAndConditions)) {
          next({ name: 'accept-terms-and-conditions' });
        } else {
          next();
        }
      } else {
        next({ name: 'not-authorized' });
      }
    } else if (store.getters['auth/getUserFlag']('acceptTermsAndConditions')) {
      store.dispatch('updateBreadcrumb', { to, from });
      store.dispatch('updateNavigationHistory', { to });
      next();
    } else if (!to.matched.some((route) => route.meta.isTermsAndConditions)) {
      next({ name: 'accept-terms-and-conditions' });
    } else {
      next();
    }
  } else if (to.matched.some((route) => route.meta.requiresGuest)) {
    if (store.getters['auth/isUserLoggedIn']) {
      next({ name: 'home' });
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
